import { lazy, Suspense, useEffect, useState } from "react";

import { AppShell } from "./components/AppShell";
import { useAuth } from "./hooks";
import { syncReadyOutbox } from "./lib/sync";
import { SignInPage } from "./pages/SignInPage";
import { HomePage } from "./pages/HomePage";
import "./incus.css";

const IncusPage = lazy(() => import("./pages/IncusPage").then((module) => ({ default: module.IncusPage })));

const IgnisPage = lazy(() =>
  import("./pages/IgnisPage").then((module) => ({
    default: module.IgnisPage,
  })),
);
const OutboxPage = lazy(() =>
  import("./pages/OutboxPage").then((module) => ({
    default: module.OutboxPage,
  })),
);
const VoiceRoutePage = lazy(() =>
  import("./pages/VoiceRoutePage").then((module) => ({
    default: module.VoiceRoutePage,
  })),
);

const OWNER_EMAIL = "andresmd01@gmail.com";

export const App = () => {
  const { loading, signIn, signOut, user } = useAuth();
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const sync = () => void syncReadyOutbox();
    window.addEventListener("online", sync);
    if (user) sync();
    return () => window.removeEventListener("online", sync);
  }, [user]);

  if (loading) {
    return (
      <div className="app-loading">
        <span className="aion-mark__sun">
          <i />
        </span>
        <span>Aion is opening Ignis…</span>
      </div>
    );
  }

  if (!user) {
    return (
      <SignInPage
        error={authError}
        onSignIn={() => {
          setAuthError(null);
          void signIn().catch(() =>
            setAuthError("Aion could not complete Google sign-in."),
          );
        }}
      />
    );
  }

  if (user.email !== OWNER_EMAIL) {
    return (
      <SignInPage
        error="This Google account is not authorized for the Aion library."
        onSignIn={() => void signOut()}
      />
    );
  }

  const path = window.location.pathname;
  const page =
    path === "/" ? <HomePage /> : path.startsWith("/incus") ? <IncusPage user={user} /> : path === "/c" ? (
      <VoiceRoutePage kind="capture" user={user} />
    ) : path === "/q" ? (
      <VoiceRoutePage kind="ask" user={user} />
    ) : path === "/outbox" ? (
      <OutboxPage user={user} />
    ) : (
      <IgnisPage user={user} />
    );

  return (
    <AppShell onSignOut={() => void signOut()} user={user}>
      <Suspense
        fallback={
          <div className="app-loading">
            <span className="aion-mark__sun">
              <i />
            </span>
            <span>Aion is opening this place…</span>
          </div>
        }
      >
        {page}
      </Suspense>
    </AppShell>
  );
};
